import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import type { WireColor } from '@/api/types/color'
import type { CenterCanvasHandle } from '@/components/app/editor/center-canvas'
import { ColorDragGhost } from '@/components/app/editor/color-drag-ghost'
import { colorFillTargets } from '@/lib/colors/color-fill-targets'
import { fillPaintForColor, fillPaintOf } from '@/lib/colors/fill-paint'
import type { ShapeManagerRef } from '@/lib/shapes/types/shape-manager-ref'
import type { BatchShapeTransformItem } from '@/lib/transforms/types/batch-shape-transform-item'
import {
  type PlacementCanvasApi,
  type PlacementDropHit,
  type PlacementIntrinsicSize,
  usePlacementSession,
} from '@/hooks/use-placement-session'

export type ColorDropHit = PlacementDropHit
export type ColorPlacementCanvasApi = PlacementCanvasApi

/** An in-flight color drag item. */
type ColorDragItem = { color: WireColor; tint: number; hex: string }

/**
 * The shape under a color drag (outlined) and the fill it would get, shown live before the drop on
 * every shape the drop would recolor (`fillShapeIds`: the shape itself, or a group's fillable
 * members).
 */
export type ColorDropPreview = { shapeId: string; fill: string; fillShapeIds: string[] }

/**
 * The shape a color was just dropped on (flashed) and the shapes it recolored; `key` changes on
 * every drop so the flash replays.
 */
export type ColorDropApplied = { shapeId: string; key: number; filledShapeIds: string[] }

type ColorPlacementContextValue = {
  beginPointerDrag: (
    item: ColorDragItem,
    clientX: number,
    clientY: number,
    thumbRect: DOMRect,
    pointerId: number,
    pointerType?: string,
    pointerCaptureEl?: HTMLElement | null
  ) => void
  cancelDrag: () => void
  isDragging: boolean
  registerCanvasApi: (api: ColorPlacementCanvasApi | null) => void
  dropTargetShapeId: string | null
  dropPreview: ColorDropPreview | null
  appliedFill: ColorDropApplied | null
}

const ColorPlacementContext = createContext<ColorPlacementContextValue | null>(null)

export function useColorPlacement(): ColorPlacementContextValue {
  const ctx = useContext(ColorPlacementContext)

  if (!ctx) {
    throw new Error('useColorPlacement must be used within ColorPlacementProvider')
  }

  return ctx
}

export function useColorPlacementOptional(): ColorPlacementContextValue | null {
  return useContext(ColorPlacementContext)
}

/** Ghost opacity while dragging a color swatch (matches photo drag). */
const COLOR_DRAG_GHOST_OPACITY = 0.9

/** How long the drop flash stays mounted; matches `color-drop-applied` in `index.css`. */
const COLOR_DROP_APPLIED_MS = 600

type ColorPlacementProviderProps = {
  children: React.ReactNode
  shapeManagerRef: React.RefObject<ShapeManagerRef | null>
  centerCanvasRef: React.RefObject<CenterCanvasHandle | null>
  /** Called when the user drops the color onto a fillable shape. */
  onFillShape?: (shapeId: string, items: BatchShapeTransformItem[]) => void
}

export function ColorPlacementProvider({
  children,
  shapeManagerRef,
  centerCanvasRef,
  onFillShape,
}: ColorPlacementProviderProps) {
  const intrinsicSizeRef = useRef<PlacementIntrinsicSize | null>(null)
  const [appliedFill, setAppliedFill] = useState<ColorDropApplied | null>(null)
  const appliedKeyRef = useRef(0)

  useEffect(() => {
    if (!appliedFill) return

    const timer = window.setTimeout(() => setAppliedFill(null), COLOR_DROP_APPLIED_MS)

    return () => window.clearTimeout(timer)
  }, [appliedFill])

  const session = usePlacementSession<ColorDragItem>({
    centerCanvasRef,
    intrinsicSizeRef,
    blockedDropMessage: 'Cannot fill this shape',
    keyboardCenterFraction: 0.5,
    displayNameOf: (item) => item.color.colorName,
    placeAtCanvas: () => {
      // Colors don't create new shapes on empty canvas — drop on empty space is a no-op.
    },
    fillShape: (item, hit, _point, _ctx) => {
      const mgr = shapeManagerRef.current

      if (!mgr) return

      const shapes = mgr.getShapes()
      const shape = shapes.find((s) => s.id === hit.shapeId)

      if (!shape) return

      // A group recolors every member that can take the fill, as one undo step.
      const targets = colorFillTargets([shape], shapes)

      if (targets.length === 0) return

      const after = fillPaintForColor(item.color, item.tint)
      const items = targets.map((t) => ({ shapeId: t.id, before: fillPaintOf(t), after }))

      for (const t of targets) mgr.updateShape(t.id, after)

      onFillShape?.(hit.shapeId, items)
      appliedKeyRef.current += 1
      setAppliedFill({
        shapeId: hit.shapeId,
        key: appliedKeyRef.current,
        filledShapeIds: targets.map((t) => t.id),
      })
    },
  })

  const { item, beginPointerDrag, cancelDrag, registerCanvasApi } = session
  const { isActive, dropTargetShapeId, ghost } = session

  const previewFill = item?.hex ?? null
  // Recomputed only when the shape under the drag changes, not on every pointer move.
  const dropPreview = useMemo<ColorDropPreview | null>(() => {
    if (dropTargetShapeId == null || previewFill == null) return null

    const shapes = shapeManagerRef.current?.getShapes() ?? []
    const shape = shapes.find((s) => s.id === dropTargetShapeId)
    const fillShapeIds = shape ? colorFillTargets([shape], shapes).map((s) => s.id) : []

    return { shapeId: dropTargetShapeId, fill: previewFill, fillShapeIds }
  }, [dropTargetShapeId, previewFill, shapeManagerRef])

  const contextValue = useMemo<ColorPlacementContextValue>(
    () => ({
      beginPointerDrag,
      cancelDrag,
      isDragging: isActive,
      registerCanvasApi,
      dropTargetShapeId,
      dropPreview,
      appliedFill,
    }),
    [
      appliedFill,
      beginPointerDrag,
      cancelDrag,
      dropPreview,
      dropTargetShapeId,
      isActive,
      registerCanvasApi,
    ]
  )

  return (
    <ColorPlacementContext.Provider value={contextValue}>
      {children}
      {item != null &&
        ghost != null &&
        createPortal(
          <ColorDragGhost color={item.hex} opacity={COLOR_DRAG_GHOST_OPACITY} {...ghost} />,
          document.body
        )}
    </ColorPlacementContext.Provider>
  )
}
